import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Table,
  Tag,
  Select,
  Empty,
  Button,
  Dropdown,
  Modal,
  Form,
  Input,
  message,
  Descriptions,
  Drawer,
  DatePicker,
  InputNumber,
  Tabs,
} from "antd";
import {
  RotateCcw,
  Calendar,
  User as UserIcon,
  TrendingUp,
  TrendingDown,
  FileText,
  DollarSign,
  Undo2,
  Lock,
} from "lucide-react";
import { EyeOutlined, EditOutlined, MoreOutlined, WhatsAppOutlined, CheckCircleOutlined } from "@ant-design/icons";
import {
  useCustomersQuery,
  useAllCustomerCreditsQuery,
  useUpdateCustomerCreditNoteMutation,
  useCustomerCreditBalanceQuery,
  useCreateCustomerCreditMutation,
  useActivePaymentMethodsQuery,
  useActiveBankAccountsQuery,
  useOrdersQuery,
  useOrderReturnsQuery,
  useCustomerOutstandingOrdersQuery,
  useSectionAccess,
} from "../../Services";
import { CustomerCreditTransactionType } from "../../Enums";
import { PaymentOption, PaymentOptionValue } from "../../components/PaymentsOption";
import { CreditAction } from "../../Types";
import dayjs from "dayjs";
import {
  PageHeader,
  drawerFooter,
  drawerTitle,
  modalTitle,
  tablePagination,
} from "../../components";
import { PAGE_SIZE } from "../../Constant";

export const CustomerLedgerPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlId = searchParams.get("id");

  const [customerId, setCustomerId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("overview");

  // Pagination states
  const [creditPage, setCreditPage] = useState(1);
  const [orderPage, setOrderPage] = useState(1);
  const [returnPage, setReturnPage] = useState(1);

  // Sorting states
  const [creditSortField, setCreditSortField] = useState<string | null>("transaction_date");
  const [creditSortOrder, setCreditSortOrder] = useState<any>("descend");

  // Repayment and note states
  const [isRepayOpen, setIsRepayOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  const [repayForm] = Form.useForm();
  const [noteForm] = Form.useForm();

  const [paymentMethodCode, setPaymentMethodCode] = useState<string>("");
  const [paymentDetails, setPaymentDetails] = useState<PaymentOptionValue>({
    amount: 0,
    note: "",
    bank_account_id: "",
    transaction_no: "",
    reference_no: "",
    cheques: [],
  });
  const [repayAmount, setRepayAmount] = useState<number>(0);

  // Queries
  const { data: customerResponse } = useCustomersQuery();
  const customers = customerResponse?.data || [];
  const { data: bankAccounts = [] } = useActiveBankAccountsQuery();
  const { data: paymentMethods = [] } = useActivePaymentMethodsQuery();

  // Set customerId from URL or when URL parameter changes
  useEffect(() => {
    if (urlId) {
      setCustomerId(urlId);
    }
  }, [urlId]);

  const selectedCustomer = customers?.find((c: any) => c.id === customerId);

  const {
    data: creditBalance,
    refetch: refetchBalance,
  } = useCustomerCreditBalanceQuery(customerId || "", {
    enabled: !!customerId,
  });

  const {
    data: creditLogs,
    isLoading: isCreditLoading,
    refetch: refetchCredits,
  } = useAllCustomerCreditsQuery(
    {
      page: creditPage,
      limit: PAGE_SIZE,
      customer_id: customerId,
      sort_by: creditSortField,
      sort_order: creditSortOrder ? (creditSortOrder === "ascend" ? "ASC" : "DESC") : null,
    },
    { enabled: !!customerId }
  );

  const {
    data: ordersData,
    isLoading: isOrdersLoading,
    refetch: refetchOrders,
  } = useOrdersQuery(
    {
      page: orderPage,
      limit: PAGE_SIZE,
      customer_id: customerId || undefined,
    },
    { enabled: !!customerId && activeTab === "orders" }
  );

  const {
    data: returnsData,
    isLoading: isReturnsLoading,
    refetch: refetchReturns,
  } = useOrderReturnsQuery(
    {
      page: returnPage,
      limit: PAGE_SIZE,
      customer_id: customerId || undefined,
    },
    { enabled: !!customerId && activeTab === "returns" }
  );

  const {
    data: outstandingOrders = [],
    isLoading: isOutstandingLoading,
    refetch: refetchOutstanding,
  } = useCustomerOutstandingOrdersQuery(customerId || "", {
    enabled: !!customerId && (activeTab === "outstanding" || isRepayOpen),
  });

  const createRepaymentMutation = useCreateCustomerCreditMutation();
  const updateNoteMutation = useUpdateCustomerCreditNoteMutation();

  const { isAllowed } = useSectionAccess("credits");

  const handleRefreshAll = () => {
    if (customerId) {
      refetchBalance();
      refetchCredits();
      refetchOrders();
      refetchReturns();
      refetchOutstanding();
    }
  };

  const handleSaveRepayment = async (values: any) => {
    try {
      if (!customerId) return;
      const pm = paymentMethods.find((m: any) => m.id === values.payment_method_id);
      if (!pm) {
        message.error("Invalid payment method.");
        return;
      }

      const res = await createRepaymentMutation.mutateAsync({
        customer_id: customerId,
        transaction_type_id: CustomerCreditTransactionType.RECEIVED,
        amount: repayAmount,
        payment_method_code: pm.code,
        bank_account_id: paymentDetails.bank_account_id || null,
        transaction_no: paymentDetails.transaction_no || null,
        reference_no: paymentDetails.reference_no || null,
        cheque_no: paymentDetails.cheque_no || null,
        bank_name: paymentDetails.bank_name || null,
        cheque_date: paymentDetails.cheque_date || null,
        note: paymentDetails.note || "",
        transaction_date: values.transaction_date ? values.transaction_date.format("YYYY-MM-DD HH:mm:ss") : null,
      });

      if (res?.success) {
        message.success(res.message || "Customer repayment recorded successfully.");
        setIsRepayOpen(false);
        repayForm.resetFields();
        setRepayAmount(0);
        setPaymentMethodCode("");
        setPaymentDetails({
          amount: 0,
          note: "",
          bank_account_id: "",
          transaction_no: "",
          reference_no: "",
          cheques: [],
        });
        handleRefreshAll();
      } else {
        message.error(res?.message || "Failed to record customer repayment.");
      }
    } catch (err) {
      console.error(err);
      message.error("An error occurred while saving the repayment.");
    }
  };

  const handleSaveNote = async (values: { note: string }) => {
    try {
      const res = await updateNoteMutation.mutateAsync({
        id: selectedRecord.id,
        note: values.note,
      });

      if (res?.success) {
        message.success(res.message || "Note updated successfully.");
        setIsEditOpen(false);
        refetchCredits();
      } else {
        message.error(res?.message || "Failed to update note.");
      }
    } catch (err) {
      console.error(err);
      message.error("An error occurred while updating the note.");
    }
  };

  // FIFO Allocation calculations for display
  const fifoAllocations = (() => {
    let remaining = repayAmount;
    return (outstandingOrders?.data || outstandingOrders || []).map((order: any) => {
      const due = Number(order.due_amount);
      if (remaining <= 0) return { ...order, allocated: 0, newDue: due };
      const allocated = Math.min(remaining, due);
      remaining -= allocated;
      return { ...order, allocated, newDue: due - allocated };
    });
  })();

  // Columns Definitions
  const creditColumns = [
    {
      title: "Date",
      dataIndex: "transaction_date",
      key: "transaction_date",
      sorter: true,
      sortOrder: creditSortField === "transaction_date" ? creditSortOrder : null,
      render: (v: string) => (
        <span className="text-xs text-gray-600 flex items-center gap-1.5 font-medium">
          <Calendar className="w-3.5 h-3.5 text-gray-400" />
          {dayjs(v).format("YYYY-MM-DD HH:mm")}
        </span>
      ),
    },
    {
      title: "Reference Order",
      dataIndex: "order_no",
      key: "order_no",
      sorter: true,
      sortOrder: creditSortField === "order_no" ? creditSortOrder : null,
      render: (v: string) =>
        v ? (
          <span className="text-xs font-bold text-blue-600">{v}</span>
        ) : (
          <span className="text-gray-300 text-xs">-</span>
        ),
    },
    {
      title: "Transaction Type",
      dataIndex: "transaction_type_name",
      key: "transaction_type_name",
      sorter: true,
      sortOrder: creditSortField === "transaction_type_name" ? creditSortOrder : null,
      render: (v: string, record: any) => {
        const code = record.transaction_type_code?.toLowerCase();
        const isIssued = code === "credit_issued" || code === "issued";
        return (
          <Tag
            color={isIssued ? "rose" : "blue"}
            className="flex items-center gap-1 w-fit text-[10px] font-bold uppercase"
          >
            {isIssued ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {v}
          </Tag>
        );
      },
    },
    {
      title: "Remarks",
      dataIndex: "note",
      key: "note",
      width: 250,
      render: (v: string) => (
        <span className="text-xs text-gray-500 block max-w-xs truncate" title={v}>
          {v || <span className="text-gray-300">No remarks</span>}
        </span>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      sorter: true,
      sortOrder: creditSortField === "amount" ? creditSortOrder : null,
      render: (v: number) => (
        <span className="font-bold text-gray-900 text-xs">
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center" as const,
      render: (_: any, record: any) => {
        const menuItems = [
          {
            key: "view",
            label: "View Details",
            icon: <EyeOutlined />,
            onClick: () => {
              setSelectedRecord(record);
              setIsDetailOpen(true);
            },
          },
          {
            key: "edit",
            label: "Edit Note",
            icon: <EditOutlined />,
            onClick: () => {
              setSelectedRecord(record);
              noteForm.setFieldsValue({ note: record.note });
              setIsEditOpen(true);
            },
          },
        ];
        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
            <Button type="text" size="small" icon={<MoreOutlined />} className="text-gray-400" />
          </Dropdown>
        );
      },
    },
  ];

  const orderColumns = [
    {
      title: "Order No",
      dataIndex: "order_no",
      key: "order_no",
      render: (v: string) => <span className="font-bold text-xs text-blue-600 font-mono">{v}</span>,
    },
    {
      title: "Date",
      dataIndex: "order_date",
      key: "order_date",
      render: (v: string) => (
        <span className="text-xs text-gray-500 font-medium">
          {dayjs(v).format("YYYY-MM-DD HH:mm")}
        </span>
      ),
    },
    {
      title: "Grand Total",
      dataIndex: "grand_total",
      key: "grand_total",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-semibold text-gray-700">Rs. {Number(v).toFixed(2)}</span>
      ),
    },
    {
      title: "Paid",
      dataIndex: "paid_amount",
      key: "paid_amount",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-medium text-emerald-600">Rs. {Number(v).toFixed(2)}</span>
      ),
    },
    {
      title: "Remaining Due",
      dataIndex: "due_amount",
      key: "due_amount",
      align: "right" as const,
      render: (v: number) => (
        <span className={`text-xs font-bold ${v > 0 ? "text-rose-600" : "text-gray-400"}`}>
          Rs. {Number(v).toFixed(2)}
        </span>
      ),
    },
  ];

  const returnColumns = [
    {
      title: "Return No",
      dataIndex: "return_no",
      key: "return_no",
      render: (v: string) => <span className="font-bold text-xs text-rose-600 font-mono">{v}</span>,
    },
    {
      title: "Order Reference",
      dataIndex: "order_no",
      key: "order_no",
      render: (v: string) => <span className="text-xs text-gray-700 font-semibold font-mono">{v}</span>,
    },
    {
      title: "Return Date",
      dataIndex: "return_date",
      key: "return_date",
      render: (v: string) => (
        <span className="text-xs text-gray-500">
          {dayjs(v).format("YYYY-MM-DD HH:mm")}
        </span>
      ),
    },
    {
      title: "Total Refunded",
      dataIndex: "total_refunded",
      key: "total_refunded",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-bold text-rose-500">Rs. {Number(v).toFixed(2)}</span>
      ),
    },
  ];

  const outstandingColumns = [
    {
      title: "Order Details",
      key: "order_no",
      render: (_: any, rec: any) => (
        <div>
          <span className="font-bold text-xs text-blue-600 block">{rec.order_no}</span>
          <span className="text-[10px] text-gray-400">{dayjs(rec.order_date).format("YYYY-MM-DD")}</span>
        </div>
      ),
    },
    {
      title: "Total Value",
      dataIndex: "grand_total",
      key: "grand_total",
      align: "right" as const,
      render: (v: number) => <span className="text-xs text-gray-600">Rs. {Number(v).toFixed(2)}</span>,
    },
    {
      title: "Remaining Due",
      dataIndex: "due_amount",
      key: "due_amount",
      align: "right" as const,
      render: (v: number) => <span className="text-xs font-bold text-rose-600">Rs. {Number(v).toFixed(2)}</span>,
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      {/* Search Header */}
      <PageHeader
        title="Customer Credit Ledger"
        subText="Detailed customer statement of accounts receivable, payments, and invoices"
        handleButtons={[
          { title: "Refresh", icon: RotateCcw as any, onClick: handleRefreshAll },
        ]}
        searchBar={
          <div className="flex items-center gap-3">
            {urlId ? (
              <Tag color="purple" className="flex items-center gap-1 text-xs py-1 px-2.5 font-semibold font-sans">
                <Lock className="w-3.5 h-3.5 text-purple-600" /> Redirect Locked Mode
              </Tag>
            ) : (
              <Select
                allowClear
                placeholder="Search Customer Profile..."
                onChange={(v) => {
                  setCustomerId(v);
                  setCreditPage(1);
                  setOrderPage(1);
                  setReturnPage(1);
                }}
                className="w-72 h-9 text-xs"
                showSearch
                optionFilterProp="children"
              >
                {customers.map((c: any) => (
                  <Select.Option key={c.id} value={c.id}>
                    {c.name} {c.phone ? `(${c.phone})` : ""}
                  </Select.Option>
                ))}
              </Select>
            )}
          </div>
        }
      />

      {!customerId ? (
        <div className="mt-8 bg-white border border-gray-100 rounded-2xl p-12 text-center shadow-sm max-w-lg mx-auto">
          <UserIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-gray-800 font-extrabold text-base mb-1">Select Customer Account</h3>
          <p className="text-gray-400 text-xs leading-relaxed">
            Please search and select a customer from the dropdown above to load their deep ledger transaction statement, orders history, sales returns, and outstanding invoices.
          </p>
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-6">
          {/* Customer Profile Summary Bar */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-6 transition-all duration-300 hover:shadow-md">
            <div className="flex flex-col gap-1.5">
              <span className="text-[10px] text-blue-600 font-extrabold uppercase tracking-wider">
                Customer Profile
              </span>
              <h2 className="text-lg font-black text-gray-800 flex items-center gap-2">
                <UserIcon className="w-5 h-5 text-gray-400" />
                {selectedCustomer?.name || "Loading name..."}
              </h2>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                {selectedCustomer?.phone && <span>Phone: <b>{selectedCustomer.phone}</b></span>}
                {selectedCustomer?.email && <span>Email: <b>{selectedCustomer.email}</b></span>}
                {selectedCustomer?.credit_limit !== undefined && (
                  <span>
                    Credit Limit: <b>Rs. {Number(selectedCustomer.credit_limit).toFixed(2)}</b>
                  </span>
                )}
              </div>
            </div>

            {creditBalance && (
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                    Outstanding Balance
                  </span>
                  <span className={`text-xl font-black ${creditBalance.balance > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                    LKR {creditBalance.balance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>

                <div className="h-10 w-[1px] bg-gray-100 hidden sm:block" />

                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                    Total Credit Borrowed
                  </span>
                  <span className="text-sm font-extrabold text-gray-800 mt-0.5">
                    LKR {creditBalance.total_issued?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>

                <div className="h-10 w-[1px] bg-gray-100 hidden sm:block" />

                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                    Total Payments Settled
                  </span>
                  <span className="text-sm font-extrabold text-gray-700 mt-0.5">
                    LKR {creditBalance.total_received?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}

            <div className="flex gap-2.5">
              {isAllowed(CreditAction.CreatePayment) && (
                <Button
                  type="primary"
                  className="bg-blue-600 hover:bg-blue-700 border-none flex items-center gap-2 h-10 px-5 text-xs font-bold rounded-lg shadow-sm hover:shadow transition-all duration-200"
                  onClick={() => {
                    repayForm.resetFields();
                    setRepayAmount(0);
                    setPaymentMethodCode("");
                    setIsRepayOpen(true);
                  }}
                >
                  Record Repayment
                </Button>
              )}

              {creditBalance && creditBalance.balance > 0 && selectedCustomer?.phone && (
                <Button
                  type="primary"
                  className="bg-emerald-600 hover:bg-emerald-700 border-none flex items-center gap-2 h-10 px-5 text-xs font-bold rounded-lg shadow-sm hover:shadow transition-all duration-200"
                  onClick={() => {
                    const phone = selectedCustomer.phone;
                    const cleanPhone = phone.replace(/[^0-9]/g, "");
                    let formattedPhone = cleanPhone;
                    if (cleanPhone.startsWith("0")) {
                      formattedPhone = "94" + cleanPhone.substring(1);
                    }
                    const messageText = `Dear ${selectedCustomer.name},\n\nThis is a friendly reminder that you have an outstanding due balance of LKR ${creditBalance.balance?.toLocaleString(undefined, { minimumFractionDigits: 2 })} on your credit account. Please let us know when you can settle this.\n\nThank you!`;
                    window.open(`https://wa.me/${formattedPhone}?text=${encodeURIComponent(messageText)}`, "_blank");
                  }}
                >
                  <WhatsAppOutlined style={{ fontSize: "1.2rem" }} />
                  Send Reminder
                </Button>
              )}
            </div>
          </div>

          {/* Details Tabs Section */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden p-6">
            <Tabs defaultActiveKey="overview" onChange={setActiveTab} className="ledger-tabs">
              {/* Tab 1: Statement Log */}
              <Tabs.TabPane
                tab={
                  <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
                    <FileText className="w-4 h-4" /> Credit Ledger Statement
                  </span>
                }
                key="overview"
              >
                <div className="pt-2">
                  <Table
                    dataSource={creditLogs?.data || creditLogs || []}
                    columns={creditColumns}
                    rowKey="id"
                    loading={isCreditLoading}
                    pagination={tablePagination({
                      current: creditPage,
                      pageSize: PAGE_SIZE,
                      total: creditLogs?.total || 0,
                      onChange: setCreditPage,
                      itemName: "logs",
                    })}
                    onChange={(pagination, filters, sorter: any) => {
                      setCreditPage(pagination.current || 1);
                      if (sorter && sorter.field) {
                        setCreditSortField(sorter.field);
                        setCreditSortOrder(sorter.order);
                      } else {
                        setCreditSortField(null);
                        setCreditSortOrder(null);
                      }
                    }}
                    scroll={{ x: 800 }}
                  />
                </div>
              </Tabs.TabPane>

              {/* Tab 2: Sales History */}
              <Tabs.TabPane
                tab={
                  <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
                    <DollarSign className="w-4 h-4" /> Billing/Orders History
                  </span>
                }
                key="orders"
              >
                <div className="pt-2">
                  <Table
                    dataSource={ordersData?.data || ordersData || []}
                    columns={orderColumns}
                    rowKey="id"
                    loading={isOrdersLoading}
                    pagination={tablePagination({
                      current: orderPage,
                      pageSize: PAGE_SIZE,
                      total: ordersData?.total || 0,
                      onChange: setOrderPage,
                      itemName: "orders",
                    })}
                    scroll={{ x: 800 }}
                  />
                </div>
              </Tabs.TabPane>

              {/* Tab 3: Returns History */}
              <Tabs.TabPane
                tab={
                  <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
                    <Undo2 className="w-4 h-4" /> Sales Returns
                  </span>
                }
                key="returns"
              >
                <div className="pt-2">
                  <Table
                    dataSource={returnsData?.data || returnsData || []}
                    columns={returnColumns}
                    rowKey="id"
                    loading={isReturnsLoading}
                    pagination={tablePagination({
                      current: returnPage,
                      pageSize: PAGE_SIZE,
                      total: returnsData?.total || 0,
                      onChange: setReturnPage,
                      itemName: "returns",
                    })}
                    scroll={{ x: 800 }}
                  />
                </div>
              </Tabs.TabPane>

              {/* Tab 4: Due/Unpaid Invoices */}
              <Tabs.TabPane
                tab={
                  <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
                    <TrendingUp className="w-4 h-4 text-rose-500" /> Outstanding Invoices
                  </span>
                }
                key="outstanding"
              >
                <div className="pt-2">
                  <Table
                    dataSource={outstandingOrders?.data || outstandingOrders || []}
                    columns={outstandingColumns}
                    rowKey="id"
                    loading={isOutstandingLoading}
                    pagination={false}
                    locale={{
                      emptyText: (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="This customer has no unpaid outstanding invoices. Outstanding Credit Balance: Rs. 0.00"
                        />
                      ),
                    }}
                  />
                </div>
              </Tabs.TabPane>
            </Tabs>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      <Drawer
        title={drawerTitle("Credit Log Details")}
        open={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        footer={drawerFooter({
          actions: [{ label: "Close", onClick: () => setIsDetailOpen(false) }],
        })}
        width={500}
      >
        {selectedRecord && (
          <Descriptions column={1} bordered className="mt-4">
            <Descriptions.Item label="Date">
              {dayjs(selectedRecord.transaction_date).format("YYYY-MM-DD HH:mm")}
            </Descriptions.Item>
            <Descriptions.Item label="Transaction Type">
              {selectedRecord.transaction_type_name}
            </Descriptions.Item>
            <Descriptions.Item label="Order Number">
              {selectedRecord.order_no || <span className="text-gray-300">-</span>}
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              LKR {selectedRecord.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </Descriptions.Item>
            <Descriptions.Item label="Remarks / Note">
              {selectedRecord.note || <span className="text-gray-300">No remarks</span>}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>

      {/* Edit Note Modal */}
      <Modal
        title={modalTitle("Edit Remarks")}
        open={isEditOpen}
        onCancel={() => setIsEditOpen(false)}
        onOk={() => noteForm.submit()}
        confirmLoading={updateNoteMutation.isPending}
        okText="Save"
        okButtonProps={{ size: "middle" }}
        cancelButtonProps={{ size: "middle" }}
      >
        <Form form={noteForm} layout="vertical" onFinish={handleSaveNote} className="mt-4">
          <Form.Item name="note" label="Remarks / Note">
            <Input.TextArea placeholder="Enter remarks..." rows={4} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Record Repayment Modal */}
      <Modal
        title={modalTitle("Record Credit Repayment")}
        open={isRepayOpen}
        onCancel={() => {
          setIsRepayOpen(false);
          repayForm.resetFields();
          setRepayAmount(0);
          setPaymentMethodCode("");
        }}
        onOk={() => repayForm.submit()}
        confirmLoading={createRepaymentMutation.isPending}
        okText="Submit Payment"
        okButtonProps={{
          size: "middle",
          disabled: repayAmount <= 0 || repayAmount > (creditBalance?.balance || 0) + 0.001,
        }}
        cancelButtonProps={{ size: "middle" }}
        width={650}
      >
        <Form
          form={repayForm}
          layout="vertical"
          onFinish={handleSaveRepayment}
          className="mt-4"
          initialValues={{ transaction_date: dayjs() }}
        >
          <Form.Item
            name="payment_method_id"
            label="Payment Method"
            rules={[{ required: true, message: "Please select payment method" }]}
          >
            <Select
              placeholder="Select payment method"
              className="h-9"
              onChange={(val) => {
                const pm = paymentMethods.find((m: any) => m.id === val);
                setPaymentMethodCode(pm ? pm.code : "");
                setPaymentDetails({
                  amount: 0,
                  note: "",
                  bank_account_id: "",
                  transaction_no: "",
                  reference_no: "",
                  cheques: [],
                });
                setRepayAmount(0);
              }}
            >
              {paymentMethods.map((pm: any) => (
                <Select.Option key={pm.id} value={pm.id}>
                  {pm.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {paymentMethodCode && (
            <div className="mb-4 pt-2 border-t border-gray-100">
              <PaymentOption
                paymentMethodCode={paymentMethodCode}
                value={paymentDetails}
                onChange={(val) => {
                  setPaymentDetails(val);
                  setRepayAmount(val.amount || 0);
                }}
                bankAccounts={bankAccounts}
              />
            </div>
          )}

          <Form.Item
            name="transaction_date"
            label="Payment Date"
            rules={[{ required: true, message: "Please select date" }]}
          >
            <DatePicker className="w-full h-9" showTime format="YYYY-MM-DD HH:mm" />
          </Form.Item>

          {/* FIFO Allocation Preview */}
          {repayAmount > 0 && outstandingOrders.length > 0 && (
            <div className="mt-4 border border-gray-100 rounded-lg p-4 bg-gray-50/50">
              <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <CheckCircleOutlined className="text-emerald-500" /> FIFO Repayment Preview
              </h4>
              <div className="max-h-48 overflow-y-auto border border-gray-100 rounded bg-white">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100 font-semibold text-gray-500">
                      <th className="p-2">Invoice</th>
                      <th className="p-2 text-right">Outstanding</th>
                      <th className="p-2 text-right text-blue-600">Allocated</th>
                      <th className="p-2 text-right">New Outstanding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fifoAllocations.map((item: any) => (
                      <tr key={item.id} className="border-b border-gray-50 last:border-0">
                        <td className="p-2 font-medium">
                          {item.order_no}
                          {item.allocated >= item.due_amount && (
                            <span className="ml-1.5 inline-flex items-center px-1 py-0.5 rounded text-[9px] font-bold bg-emerald-50 text-emerald-600">
                              ✓ Settled
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-right">Rs. {Number(item.due_amount).toFixed(2)}</td>
                        <td className="p-2 text-right font-bold text-blue-600">
                          {item.allocated > 0 ? `Rs. ${item.allocated.toFixed(2)}` : "—"}
                        </td>
                        <td className="p-2 text-right font-medium text-gray-700">
                          Rs. {item.newDue.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};
